import SingleLineText from '../components/ui/SingleLineText';
import Button from '../components/ui/FitButton';
import React, { useState } from 'react';
import { Box, Typography, Card, CardContent, Stack, Slider } from '@mui/material';
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PageHeader from '../components/PageHeader';
import PetCharacter from '../components/character/PetCharacter';
import XpGauge from '../components/character/XpGauge';
import { LEVEL_LABELS, LEVEL_COUNT } from '../components/character/stages';

const XP_THRESHOLDS = [0, 1, 21, 51, 151, 351, 701, 1201, 3001, 9000, 14000, 20000, 32000];
const STAGE_DAYS = ['시작 전', '~1일', '~3일', '~6일', '~14일', '~30일', '~60일', '~100일', '~365일', '~500일', '~730일', '~1000일', '1500일+'];

// 미리보기 페이지 — 모든 디자인 시나리오를 한 곳에서 검수.
// 단계/진행률/streak 슬라이더 + 점프/진화/fly-away 트리거 버튼
export default function CharacterPreviewPage() {
    const [level, setLevel] = useState(4);
    const [progress, setProgress] = useState(0.6);
    const [streak, setStreak] = useState(18);
    const [petJumpTrigger, setPetJumpTrigger] = useState(0);
    const [evolving, setEvolving] = useState(false);
    const [flyAway, setFlyAway] = useState(false);

    const safeLv = Math.max(0, Math.min(LEVEL_COUNT - 1, level));
    const nextLv = safeLv < LEVEL_COUNT - 1 ? safeLv + 1 : null;
    const xpForNextLevel = nextLv != null ? XP_THRESHOLDS[nextLv] - XP_THRESHOLDS[safeLv] : 0;
    const currentLevelXp = Math.round(progress * xpForNextLevel);
    const levelLabel = LEVEL_LABELS[safeLv];
    const nextLevelLabel = nextLv != null ? LEVEL_LABELS[nextLv] : null;

    const character = {
        level: safeLv,
        levelLabel,
        nextLevelLabel,
        currentLevelXp,
        xpForNextLevel,
        progress,
        streak,
        totalXp: XP_THRESHOLDS[safeLv] + currentLevelXp,
    };

    const triggerJump = () => setPetJumpTrigger(t => t + 1);
    const triggerEvolve = () => {
        setEvolving(true);
        setPetJumpTrigger(t => t + 1);
        setTimeout(() => setEvolving(false), 1500);
    };
    const triggerFlyAway = () => {
        if (flyAway) return;
        setFlyAway(true);
        setTimeout(() => setFlyAway(false), 3500);
    };

    return (
        <Box sx={{ pb: 10, bgcolor: '#FAFAFA', minHeight: '100vh' }}>
            <PageHeader title="캐릭터 미리보기" back />

            <Box sx={{ px: 2, pt: 1, pb: 2 }}>
                {/* 컨트롤 패널 */}
                <Card sx={{ borderRadius: 3, mb: 2 }}>
                    <CardContent sx={{ py: 2 }}>
                        <Typography sx={{ fontSize: '0.85rem', color: '#666', mb: 0.5 }}>
                            레벨 (L{safeLv} · {levelLabel})
                        </Typography>
                        <Slider
                            value={safeLv}
                            onChange={(_, v) => setLevel(v)}
                            min={0} max={12} step={1} marks
                            valueLabelDisplay="auto"
                            sx={{ mb: 2 }}
                        />

                        <Typography sx={{ fontSize: '0.85rem', color: '#666', mb: 0.5 }}>
                            진행률 ({Math.round(progress * 100)}% · {currentLevelXp}/{xpForNextLevel}점)
                        </Typography>
                        <Slider
                            value={progress}
                            onChange={(_, v) => setProgress(v)}
                            min={0} max={1} step={0.01}
                            valueLabelDisplay="auto"
                            valueLabelFormat={(v) => `${Math.round(v * 100)}%`}
                            sx={{ mb: 2 }}
                        />

                        <Typography sx={{ fontSize: '0.85rem', color: '#666', mb: 0.5 }}>
                            연속 일수 ({streak}일)
                        </Typography>
                        <Slider
                            value={streak}
                            onChange={(_, v) => setStreak(v)}
                            min={0} max={365} step={1}
                            valueLabelDisplay="auto"
                            sx={{ mb: 2 }}
                        />

                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <Button size="small" variant="contained" onClick={triggerJump} sx={{ borderRadius: 2 }}>
                                점프
                            </Button>
                            <Button size="small" variant="contained" color="warning" onClick={triggerEvolve} sx={{ borderRadius: 2 }}>
                                ✨ 진화 연출
                            </Button>
                            <Button size="small" variant="contained" color="error" onClick={triggerFlyAway} sx={{ borderRadius: 2 }}>
                                🚀 화면 밖으로
                            </Button>
                        </Stack>
                    </CardContent>
                </Card>

                {/* 1. 헤더 우측 미리보기 */}
                <SingleLineText sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#666', mb: 1, mt: 2 }}>
                    1. 홈 헤더 우측 영역
                </SingleLineText>
                <Card sx={{ borderRadius: 3, mb: 2 }}>
                    <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#fff', borderRadius: 3 }}>
                        <SingleLineText sx={{ fontSize: '1.2rem', fontWeight: 800, color: '#1a1a1a' }}>
                            오늘의 약
                        </SingleLineText>
                        <Box sx={{ width: 124, height: 56, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box sx={{ width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'visible' }}>
                                    <PetCharacter
                                        level={safeLv}
                                        size={34}
                                        jumpTrigger={petJumpTrigger}
                                        evolving={evolving}
                                        flyAway={flyAway}
                                    />
                                </Box>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: '#9a9a9a', lineHeight: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        Lv.{safeLv} {levelLabel}
                                    </Typography>
                                    {streak > 0 && (
                                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.2, lineHeight: 1, mt: 0.3, whiteSpace: 'nowrap' }}>
                                            <Typography component="span" sx={{ fontSize: '0.85rem', fontWeight: 800, color: '#1a1a1a', lineHeight: 1 }}>
                                                {streak}
                                            </Typography>
                                            <Typography component="span" sx={{ fontSize: '0.6rem', fontWeight: 600, color: '#666', lineHeight: 1 }}>
                                                일 연속 복용
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                            <Box sx={{ width: '100%', mt: 0.5 }}>
                                <XpGauge
                                    progress={progress}
                                    level={safeLv}
                                    size="mini"
                                    currentXp={currentLevelXp}
                                    totalXp={xpForNextLevel}
                                />
                            </Box>
                        </Box>
                    </Box>
                </Card>

                {/* 2. 통계 페이지 카드 미리보기 */}
                <SingleLineText sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#666', mb: 1, mt: 2 }}>
                    2. 통계 페이지 카드
                </SingleLineText>
                <Card sx={{ borderRadius: 4, mb: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                    <CardContent sx={{ py: 1.8, '&:last-child': { pb: 1.8 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ width: 60, height: 60, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'visible' }}>
                                <PetCharacter
                                    level={safeLv}
                                    size={60}
                                    jumpTrigger={petJumpTrigger}
                                    evolving={evolving}
                                    flyAway={flyAway}
                                />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                {streak > 0 ? (
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, lineHeight: 1 }}>
                                        <Typography sx={{ fontSize: '1.6rem', fontWeight: 900, color: '#1a1a1a', letterSpacing: '-0.03em', lineHeight: 1 }}>
                                            {streak}
                                        </Typography>
                                        <SingleLineText sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#1a1a1a' }}>
                                            일 연속 복용 중
                                        </SingleLineText>
                                    </Box>
                                ) : (
                                    <SingleLineText sx={{ fontSize: '1.15rem', fontWeight: 800, color: '#1a1a1a', lineHeight: 1.2 }}>
                                        오늘부터 다시 시작해봐요
                                    </SingleLineText>
                                )}
                                <Typography sx={{ fontSize: '0.78rem', color: '#888', mt: 0.4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    Lv.{safeLv} {levelLabel}
                                </Typography>
                                {!nextLevelLabel ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.6 }}>
                                        <EmojiEventsIcon sx={{ fontSize: 18, color: '#E48B1F' }} />
                                        <SingleLineText sx={{ fontSize: '0.85rem', color: '#E48B1F', fontWeight: 700 }}>
                                            최종 단계 도달!
                                        </SingleLineText>
                                    </Box>
                                ) : (
                                    <>
                                        <Box sx={{ mt: 0.6 }}>
                                            <XpGauge
                                                progress={progress}
                                                level={safeLv}
                                                size="large"
                                                currentXp={currentLevelXp}
                                                totalXp={xpForNextLevel}
                                            />
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 0.7 }}>
                                            <TipsAndUpdatesOutlinedIcon sx={{ fontSize: 14, color: '#aaa' }} />
                                            <SingleLineText sx={{ fontSize: '0.72rem', color: '#999', lineHeight: 1 }}>
                                                연속 복용하면 점수가 더 많이 올라요
                                            </SingleLineText>
                                        </Box>
                                    </>
                                )}
                            </Box>
                        </Box>

                        <Box sx={{ mt: 2, pt: 1.8, borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-around' }}>
                            <Box sx={{ textAlign: 'center', flex: 1 }}>
                                <Typography sx={{ fontSize: '1.15rem', fontWeight: 800, color: '#1a1a1a', lineHeight: 1.1 }}>
                                    {character.totalXp.toLocaleString()}
                                    <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#666', ml: 0.2 }}>점</Typography>
                                </Typography>
                                <SingleLineText sx={{ fontSize: '0.72rem', color: '#888', mt: 0.4 }}>누적 점수</SingleLineText>
                            </Box>
                            <Box sx={{ width: '1px', bgcolor: '#f0f0f0', mx: 1 }} />
                            <Box sx={{ textAlign: 'center', flex: 1 }}>
                                <Typography sx={{ fontSize: '1.15rem', fontWeight: 800, color: '#1a1a1a', lineHeight: 1.1 }}>
                                    {streak}
                                    <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#666', ml: 0.2 }}>일</Typography>
                                </Typography>
                                <SingleLineText sx={{ fontSize: '0.72rem', color: '#888', mt: 0.4 }}>최고 연속</SingleLineText>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>

                {/* 3. 전체 단계 갤러리 */}
                <SingleLineText sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#666', mb: 1, mt: 2 }}>
                    3. 전 단계 갤러리 (13개)
                </SingleLineText>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
                    {Array.from({ length: 13 }).map((_, lv) => (
                        <Card key={lv} sx={{ borderRadius: 2, p: 1, textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                            <Box sx={{ width: '100%', height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <PetCharacter level={lv} size={56} />
                            </Box>
                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#1a1a1a', mt: 0.5 }}>
                                L{lv}
                            </Typography>
                            <Typography sx={{ fontSize: '0.65rem', color: '#888', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {LEVEL_LABELS[lv]}
                            </Typography>
                            <Typography sx={{ fontSize: '0.6rem', color: '#aaa', mt: 0.2 }}>
                                {STAGE_DAYS[lv]}
                            </Typography>
                        </Card>
                    ))}
                </Box>
            </Box>
        </Box>
    );
}
